/**
 * Order Confirmation View Modal
 * Displays order confirmation email and WhatsApp templates for quotes with existing orders
 */

import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Space,
  message,
  Card,
  Typography,
  Tabs,
  Spin,
  Input,
} from "antd";
import {
  CopyOutlined,
  CheckOutlined,
  MessageOutlined,
  MailOutlined,
  FilePdfOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import api from "../../../shared/api";
import {
  generateOrderConfirmationWhatsApp,
  generateOrderConfirmationEmail,
} from "../services/orderConfirmationTemplateService";
import { copyToClipboard } from "../../leads/services/communicationTemplateService";
import { getReceiptAndTemplates, getDepositReceipt } from "../services/depositService";

const { TextArea } = Input;
const { Text } = Typography;
const { TabPane } = Tabs;

const OrderConfirmationViewModal = ({ visible, quote, orderId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [whatsappCopied, setWhatsappCopied] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const [whatsappTemplate, setWhatsappTemplate] = useState("");
  const [emailTemplate, setEmailTemplate] = useState({ subject: "", body: "" });
  const [order, setOrder] = useState(null);
  const [receiptPdfUrl, setReceiptPdfUrl] = useState(null);
  const [receiptData, setReceiptData] = useState(null);
  const [hasReceipt, setHasReceipt] = useState(false);

  // Load order data and generate templates
  useEffect(() => {
    if (visible && quote) {
      loadOrderAndGenerateTemplates();
    }

    // Cleanup when modal closes
    return () => {
      if (!visible && receiptPdfUrl) {
        URL.revokeObjectURL(receiptPdfUrl);
        setReceiptPdfUrl(null);
        setHasReceipt(false);
        setReceiptData(null);
      }
    };
  }, [visible, quote?.id, orderId]);

  const loadOrderAndGenerateTemplates = async () => {
    setLoading(true);
    try {
      let orderData;
      
      // If we have an orderId, fetch directly
      if (orderId) {
        const orderResponse = await api.get(`/orders/${orderId}`);
        orderData = orderResponse.data || orderResponse;
      } else {
        // Otherwise, find order by quoteId
        const ordersResponse = await api.get(`/orders?quoteId=${quote.id}`);
        const orders = ordersResponse.data || ordersResponse;
        if (Array.isArray(orders) && orders.length > 0) {
          orderData = orders[0];
        } else if (orders && !Array.isArray(orders)) {
          orderData = orders;
        } else {
          throw new Error("Order not found for this quote");
        }
      }

      setOrder(orderData);

      // Get payment reference from quote
      const paymentReference =
        quote.payment?.paymentReference || "N/A";
      
      // Determine confirmation type
      const confirmationType = quote.payment?.proofReceived
        ? "proof_received"
        : quote.payment?.invoiceReceived
        ? "money_cleared"
        : "money_cleared"; // default

      // Generate templates
      const whatsapp = generateOrderConfirmationWhatsApp(
        quote,
        orderData,
        paymentReference,
        confirmationType
      );
      const email = generateOrderConfirmationEmail(
        quote,
        orderData,
        paymentReference,
        confirmationType
      );

      setWhatsappTemplate(whatsapp);
      setEmailTemplate(email);

      // Check if deposit receipt is available
      await checkForReceipt();
    } catch (error) {
      console.error("Error loading order:", error);
      message.error("Failed to load order confirmation templates");
    } finally {
      setLoading(false);
    }
  };

  const checkForReceipt = async () => {
    // Check if deposit was verified (which would have a receipt)
    const isDepositVerified =
      quote.payment?.depositVerified === true ||
      quote.status === "deposit_verified";

    if (isDepositVerified) {
      try {
        const result = await getReceiptAndTemplates(quote.id);
        if (result.success && result.data?.receipt) {
          setHasReceipt(true);
          setReceiptData(result.data);

          // Create PDF blob URL from base64
          if (result.data.receipt?.pdfBase64) {
            const pdfBlob = new Blob(
              [
                Uint8Array.from(atob(result.data.receipt.pdfBase64), (c) =>
                  c.charCodeAt(0)
                ),
              ],
              { type: "application/pdf" }
            );
            const url = URL.createObjectURL(pdfBlob);
            setReceiptPdfUrl(url);
          }
        }
      } catch (error) {
        console.error("Error checking for receipt:", error);
        // Don't show error - receipt is optional
      }
    }
  };

  const handleDownloadReceipt = async () => {
    if (!quote?.id) return;

    try {
      const blob = await getDepositReceipt(quote.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const filename =
        receiptData?.receipt?.filename ||
        `Receipt-${order?.id || order?.orderId || quote.id}.pdf`;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success("Receipt downloaded successfully!");
    } catch (error) {
      console.error("Error downloading receipt:", error);
      message.error(error.message || "Failed to download receipt");
    }
  };

  const handleCopyWhatsApp = async () => {
    if (!whatsappTemplate) return;

    const success = await copyToClipboard(whatsappTemplate);
    if (success) {
      setWhatsappCopied(true);
      message.success("WhatsApp template copied to clipboard!");
      setTimeout(() => setWhatsappCopied(false), 2000);
    } else {
      message.error("Failed to copy to clipboard");
    }
  };

  const handleCopyEmail = async () => {
    if (!emailTemplate.body) return;

    const emailText = `Subject: ${emailTemplate.subject}\n\n${emailTemplate.body}`;
    const success = await copyToClipboard(emailText);
    if (success) {
      setEmailCopied(true);
      message.success("Email template copied to clipboard!");
      setTimeout(() => setEmailCopied(false), 2000);
    } else {
      message.error("Failed to copy to clipboard");
    }
  };

  return (
    <Modal
      title={
        <Space>
          <MailOutlined />
          <span>Order Confirmation Templates</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={900}
      destroyOnHidden
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <Spin size="large" />
          <div style={{ marginTop: "16px" }}>
            <Text>Loading confirmation templates...</Text>
          </div>
        </div>
      ) : (
        <div>
          <div style={{ marginBottom: 24 }}>
            <Text>
              {order?.id || order?.orderId ? (
                <>
                  Order <Text strong>{order.id || order.orderId}</Text> confirmation templates for
                  quote <Text strong>{quote.id}</Text>
                </>
              ) : (
                <>
                  Confirmation templates for quote <Text strong>{quote.id}</Text>
                </>
              )}
            </Text>
          </div>

          {/* Receipt Preview - Only show if deposit receipt is available */}
          {hasReceipt && receiptPdfUrl && (
            <Card
              title={
                <Space>
                  <FilePdfOutlined />
                  <span>Deposit Receipt</span>
                </Space>
              }
              style={{ marginBottom: 16 }}
              extra={
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={handleDownloadReceipt}
                >
                  Download Receipt
                </Button>
              }
            >
              <iframe
                src={receiptPdfUrl}
                style={{
                  width: "100%",
                  height: "500px",
                  border: "1px solid #d9d9d9",
                  borderRadius: "4px",
                }}
                title="Receipt Preview"
              />
            </Card>
          )}

          {/* Message Templates */}
          <Card
            title={
              <Space>
                <MessageOutlined />
                <span>Communication Templates</span>
              </Space>
            }
            style={{ marginBottom: 16 }}
          >
            <Tabs defaultActiveKey="whatsapp">
              <TabPane
                tab={
                  <Space>
                    <MessageOutlined />
                    <span>WhatsApp</span>
                  </Space>
                }
                key="whatsapp"
              >
                <div style={{ marginBottom: 12 }}>
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    Copy this template to send via WhatsApp
                  </Text>
                </div>
                <TextArea
                  value={whatsappTemplate}
                  readOnly
                  rows={15}
                  style={{ fontFamily: "monospace", fontSize: "13px" }}
                />
                <div style={{ marginTop: 12, textAlign: "right" }}>
                  <Button
                    type="primary"
                    icon={whatsappCopied ? <CheckOutlined /> : <CopyOutlined />}
                    onClick={handleCopyWhatsApp}
                  >
                    {whatsappCopied ? "Copied!" : "Copy WhatsApp Template"}
                  </Button>
                </div>
              </TabPane>

              <TabPane
                tab={
                  <Space>
                    <MailOutlined />
                    <span>Email</span>
                  </Space>
                }
                key="email"
              >
                <div style={{ marginBottom: 12 }}>
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    Copy this template to send via Email
                  </Text>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <Text strong>Subject: </Text>
                  <Text code>{emailTemplate.subject}</Text>
                </div>
                <TextArea
                  value={emailTemplate.body}
                  readOnly
                  rows={12}
                  style={{ fontFamily: "monospace", fontSize: "13px" }}
                />
                <div style={{ marginTop: 12, textAlign: "right" }}>
                  <Button
                    type="primary"
                    icon={emailCopied ? <CheckOutlined /> : <CopyOutlined />}
                    onClick={handleCopyEmail}
                  >
                    {emailCopied ? "Copied!" : "Copy Email Template"}
                  </Button>
                </div>
              </TabPane>
            </Tabs>
          </Card>

          <div style={{ textAlign: "right", marginTop: 16 }}>
            <Button type="primary" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default OrderConfirmationViewModal;

